/**
 * Payout API Explorer entries.
 *
 * Backed by DeveloperPortalPayoutController (/payout/dp) and
 * DeveloperPortalPayoutBatchController (/payout/dp/batches) in MerchantPortal.
 * Request/response bodies documented here are the plaintext shapes.
 *
 */
export const PAYOUT_API_CONFIGS: Record<string, any> = {

  'payout-beneficiary-add': {
    title: 'Add New or Reactivate Beneficiary',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/beneficiaries/add',
        method: 'POST',
        tag: 'Payout',
        title: 'Add New or Reactivate Beneficiary',
        description: 'Registers a bank beneficiary against the caller’s account. An inactive beneficiary matching the same bank branch and IBAN is reactivated instead of duplicated; an active match is rejected with ERROR_DUPLICATE_BENEFICIARY. New records are stored as ACTIVE, type BANK, relation SELF, account type SAVINGS. Guarded by @EncryptedRequestForPortal.',
        operationId: 'payoutBeneficiaryAdd',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'bankBranchId', required: true, description: 'Bank branch ID. Resolve it from the IBAN validation response, or from bankList when manualBankSelectionRequired is true.', schema: { type: 'integer', example: 12 } },
          { name: 'iban', required: true, description: 'Beneficiary IBAN.', schema: { type: 'string', example: 'AE070331234567890123456' } },
          { name: 'accountHolderName', required: true, description: 'Account holder name as registered with the bank.', schema: { type: 'string', example: 'Ahmed Al Mansouri' } },
          { name: 'confirmed', required: true, fixed: true, description: 'User confirmation flag. Must be true - the backend rejects an unconfirmed request.', schema: { type: 'boolean', example: true } },
          { name: 'addedVia', required: false, description: 'Source channel. Defaults to MERCHANT_PORTAL when omitted.', schema: { type: 'string', example: 'MERCHANT_PORTAL' } }
        ],
        responses: [
          {
            status: '200',
            description: 'Success',
            content: { response: { data: { id: 101, bankBranchId: 12, bankBranchCode: 'ADCBAEAA', bankBranchName: 'Abu Dhabi Main', bankCode: 'ADCB', bankName: 'Abu Dhabi Commercial Bank', bankIcon: 'https://cdn.pay10.com/banks/adcb.svg', countryCode: 'AE', countryName: 'United Arab Emirates', accountNoMasked: '****3456', accountHolderName: 'Ahmed Al Mansouri', accountType: 'SAVINGS', relation: 'SELF', status: 'ACTIVE', addedVia: 'MERCHANT_PORTAL', createDate: '2026-08-24T11:41:02Z', updateDate: '2026-08-24T11:41:02Z', totalPayoutsLtd: 14, totalValuePaidLtd: 12750.0 }, status: 200, httpStatus: 'OK', message: 'Beneficiary added successfully' } },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'id', type: 'integer', description: 'Beneficiary ID, used as beneficiaryId in preview, transfer and schedule calls' },
              { name: 'bankBranchId', type: 'integer', description: 'Bank branch ID' },
              { name: 'bankName', type: 'string', description: 'Bank name' },
              { name: 'bankIcon', type: 'string', description: 'Bank logo URL' },
              { name: 'countryCode', type: 'string', description: 'ISO country code' },
              { name: 'accountNoMasked', type: 'string', description: 'Masked account number' },
              { name: 'accountHolderName', type: 'string', description: 'Account holder name' },
              { name: 'accountType', type: 'string', description: 'Account type - SAVINGS for beneficiaries added through this API' },
              { name: 'relation', type: 'string', description: 'Relation to the account holder - SELF' },
              { name: 'status', type: 'string', description: 'ACTIVE or INACTIVE' },
              { name: 'addedVia', type: 'string', description: 'Channel the beneficiary was added through' },
              { name: 'totalPayoutsLtd', type: 'integer', description: 'Lifetime number of payouts to this beneficiary' },
              { name: 'totalValuePaidLtd', type: 'number', description: 'Lifetime value paid to this beneficiary' },
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'response.message', type: 'string', description: 'Human-readable result message' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request - validation failure',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/beneficiaries/add' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized - missing or expired access token',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/beneficiaries/add' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '403',
            description: 'Forbidden - no encryption key mapping for this user in the selected environment',
            content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          },
          {
            status: '409',
            description: 'Conflict - an active beneficiary with the same IBAN already exists',
            content: { status: 409, httpStatus: 'CONFLICT', message: 'ERROR_DUPLICATE_BENEFICIARY' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'ERROR_DUPLICATE_BENEFICIARY when an active duplicate exists' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Beneficiary added successfully', contentType: 'JSON', content: { response: { data: { id: 101, bankBranchId: 12, bankBranchCode: 'ADCBAEAA', bankBranchName: 'Abu Dhabi Main', bankCode: 'ADCB', bankName: 'Abu Dhabi Commercial Bank', bankIcon: 'https://cdn.pay10.com/banks/adcb.svg', countryCode: 'AE', countryName: 'United Arab Emirates', accountNoMasked: '****3456', accountHolderName: 'Ahmed Al Mansouri', accountType: 'SAVINGS', relation: 'SELF', status: 'ACTIVE', addedVia: 'MERCHANT_PORTAL', createDate: '2026-08-24T11:41:02Z', updateDate: '2026-08-24T11:41:02Z', totalPayoutsLtd: 14, totalValuePaidLtd: 12750.0 }, status: 200, httpStatus: 'OK', message: 'Beneficiary added successfully' } } },
        failureResponse: { status: '400', description: 'Bad Request - validation failure', contentType: 'JSON', content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/beneficiaries/add' } }
      }
    ],
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' }
  },

  'payout-beneficiary-validate-iban': {
    title: 'Validate IBAN',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/beneficiaries/validate/{iban}',
        method: 'GET',
        tag: 'Payout',
        title: 'Validate IBAN',
        description: 'Validates an IBAN through the Product service and resolves the owning bank. When the bank or branch cannot be resolved, manualBankSelectionRequired is true and bankList carries the active banks to choose from. The account name is masked before it is returned.',
        operationId: 'payoutBeneficiaryValidateIban',
        parameters: [
          { name: 'iban', in: 'path', required: true, description: 'IBAN to validate.', schema: { type: 'string', example: 'AE070331234567890123456' } },
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Success',
            content: { response: { data: { accountName: 'Ah*** Al M*****', iban: 'AE070331234567890123456', bankName: 'Abu Dhabi Commercial Bank', bankBranchId: 12, manualBankSelectionRequired: false, bankList: null }, status: 200, httpStatus: 'OK', message: 'IBAN validated successfully' } },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'accountName', type: 'string', description: 'Account holder name, masked before it leaves the controller' },
              { name: 'iban', type: 'string', description: 'The validated IBAN' },
              { name: 'bankName', type: 'string', description: 'Resolved bank name' },
              { name: 'bankBranchId', type: 'integer', description: 'Bank branch ID to pass to the add-beneficiary call' },
              { name: 'manualBankSelectionRequired', type: 'boolean', description: 'True when the bank could not be resolved - pick one from bankList instead' },
              { name: 'bankList', type: 'object', description: 'Active banks to choose from when manual selection is required' },
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'response.message', type: 'string', description: 'Human-readable result message' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request - validation failure',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/beneficiaries/validate/{iban}' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized - missing or expired access token',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/beneficiaries/validate/{iban}' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '403',
            description: 'Forbidden - no encryption key mapping for this user in the selected environment',
            content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'IBAN validated successfully', contentType: 'JSON', content: { response: { data: { accountName: 'Ah*** Al M*****', iban: 'AE070331234567890123456', bankName: 'Abu Dhabi Commercial Bank', bankBranchId: 12, manualBankSelectionRequired: false, bankList: null }, status: 200, httpStatus: 'OK', message: 'IBAN validated successfully' } } },
        failureResponse: { status: '400', description: 'Bad Request - validation failure', contentType: 'JSON', content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/beneficiaries/validate/{iban}' } }
      }
    ],
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' }
  },

  'payout-eligibility-today': {
    title: 'Daily Payout Eligibility',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/eligibility/today',
        method: 'GET',
        tag: 'Payout',
        title: 'Daily Payout Eligibility',
        description: 'Resolves today’s payout allowance. Counts payouts already used, folds in schedules due today, advances any missed schedules, and resolves the min/max transaction amounts and monthly value usage. Limits resolve through VelocityConfig[userId] → VelocityConfig[DEFAULT_MERCHANT] → PayoutCountryConfig → ProductSlab → default of 5.',
        operationId: 'payoutEligibilityToday',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Success',
            content: { response: { data: { isAllowed: true, dailyLimit: 5, usedToday: 2, remainingToday: 3, message: 'Payout allowed', minTransactionAmount: 10.0, maxTransactionAmount: 50000.0, minTotalAmount: 10.0, maxTotalAmount: 100000.0, monthlyAmountLimit: 500000.0, monthlyAmountUsed: 87250.0, monthlyAmountRemaining: 412750.0 }, status: 200, httpStatus: 'OK', message: 'Eligibility fetched successfully' } },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'isAllowed', type: 'boolean', description: 'Whether a payout may be initiated right now' },
              { name: 'dailyLimit', type: 'integer', description: 'Maximum payouts per day, resolved from the limit chain' },
              { name: 'usedToday', type: 'integer', description: 'Payouts already used today, including schedules due today' },
              { name: 'remainingToday', type: 'integer', description: 'Remaining payouts for today' },
              { name: 'message', type: 'string', description: 'Reason when isAllowed is false' },
              { name: 'minTransactionAmount', type: 'number', description: 'Minimum amount for a single transfer' },
              { name: 'maxTransactionAmount', type: 'number', description: 'Maximum amount for a single transfer' },
              { name: 'minTotalAmount', type: 'number', description: 'Minimum total across a batch' },
              { name: 'maxTotalAmount', type: 'number', description: 'Maximum total across a batch' },
              { name: 'monthlyAmountLimit', type: 'number', description: 'Monthly payout value cap' },
              { name: 'monthlyAmountUsed', type: 'number', description: 'Monthly value already used' },
              { name: 'monthlyAmountRemaining', type: 'number', description: 'Monthly value still available' },
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'response.message', type: 'string', description: 'Human-readable result message' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request - validation failure',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/eligibility/today' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized - missing or expired access token',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/eligibility/today' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '403',
            description: 'Forbidden - no encryption key mapping for this user in the selected environment',
            content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Eligibility fetched successfully', contentType: 'JSON', content: { response: { data: { isAllowed: true, dailyLimit: 5, usedToday: 2, remainingToday: 3, message: 'Payout allowed', minTransactionAmount: 10.0, maxTransactionAmount: 50000.0, minTotalAmount: 10.0, maxTotalAmount: 100000.0, monthlyAmountLimit: 500000.0, monthlyAmountUsed: 87250.0, monthlyAmountRemaining: 412750.0 }, status: 200, httpStatus: 'OK', message: 'Eligibility fetched successfully' } } },
        failureResponse: { status: '400', description: 'Bad Request - validation failure', contentType: 'JSON', content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/eligibility/today' } }
      }
    ],
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' }
  },

  'payout-eligibility-future': {
    title: 'Scheduling Future Eligibility',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/eligibility/future',
        method: 'GET',
        tag: 'Payout',
        title: 'Scheduling Future Eligibility',
        description: 'Checks whether a future date, or every execution date generated from a start date, end date and frequency, stays within the daily payout limit. You must supply EITHER date on its own, OR all three of startDate, endDate and frequency - sending none of them is rejected with 400. Used to validate a schedule before creating it.',
        operationId: 'payoutEligibilityFuture',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'date', in: 'query', required: false, description: 'Single date to evaluate (yyyy-MM-dd). Required unless you supply startDate, endDate and frequency.', schema: { type: 'string', example: '2026-09-01' } },
          { name: 'startDate', in: 'query', required: false, description: 'Start of the range (yyyy-MM-dd). Required with endDate and frequency, unless you supply date.', schema: { type: 'string', example: '2026-09-01' } },
          { name: 'endDate', in: 'query', required: false, description: 'End of the range (yyyy-MM-dd). Required with startDate and frequency, unless you supply date.', schema: { type: 'string', example: '2027-08-31' } },
          { name: 'frequency', in: 'query', required: false, description: 'Cadence for generating execution dates. Required with startDate and endDate, unless you supply date.', schema: { type: 'string', example: 'MONTHLY', enum: ['DAILY', 'WEEKLY', 'MONTHLY'] } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Success',
            content: {
              response: {
                data: {
                  isAllowed: true,
                  dailyLimit: 5,
                  message: 'All requested dates are within limits',
                  minTransactionAmount: 10.0,
                  maxTransactionAmount: 50000.0,
                  minTotalAmount: 10.0,
                  maxTotalAmount: 100000.0,
                  schedulerCutoffTime: '18:00:00',
                  targetDate: '2026-09-01',
                  scheduledCount: 1,
                  executedCount: 0,
                  usedCount: 1,
                  remaining: 4,
                  dates: [
                    { date: '2026-09-01', isAllowed: true, scheduledCount: 1, executedCount: 0, usedCount: 1, remaining: 4, message: 'Within daily limit' }
                  ]
                },
                status: 200,
                httpStatus: 'OK',
                message: 'Future eligibility fetched successfully'
              }
            },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'isAllowed', type: 'boolean', description: 'True only when every generated execution date is within the daily limit' },
              { name: 'dailyLimit', type: 'integer', description: 'Daily payout count limit' },
              { name: 'schedulerCutoffTime', type: 'string', description: 'Cut-off after which a schedule due today is treated as missed' },
              { name: 'targetDate', type: 'string', description: 'Evaluated date when a single date was supplied' },
              { name: 'scheduledCount', type: 'integer', description: 'Payouts already scheduled for the date' },
              { name: 'executedCount', type: 'integer', description: 'Payouts already executed on the date' },
              { name: 'usedCount', type: 'integer', description: 'scheduledCount + executedCount' },
              { name: 'remaining', type: 'integer', description: 'Remaining capacity on the date' },
              { name: 'dates', type: 'object', description: 'Per-date breakdown when startDate, endDate and frequency are supplied' },
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'response.message', type: 'string', description: 'Human-readable result message' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request - validation failure',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/eligibility/future' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized - missing or expired access token',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/eligibility/future' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '403',
            description: 'Forbidden - no encryption key mapping for this user in the selected environment',
            content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Future eligibility fetched successfully',
          contentType: 'JSON',
          content: {
            response: {
              data: {
                isAllowed: true,
                dailyLimit: 5,
                message: 'All requested dates are within limits',
                minTransactionAmount: 10.0,
                maxTransactionAmount: 50000.0,
                minTotalAmount: 10.0,
                maxTotalAmount: 100000.0,
                schedulerCutoffTime: '18:00:00',
                targetDate: '2026-09-01',
                scheduledCount: 1,
                executedCount: 0,
                usedCount: 1,
                remaining: 4,
                dates: [
                  { date: '2026-09-01', isAllowed: true, scheduledCount: 1, executedCount: 0, usedCount: 1, remaining: 4, message: 'Within daily limit' }
                ]
              },
              status: 200,
              httpStatus: 'OK',
              message: 'Future eligibility fetched successfully'
            }
          }
        },
        failureResponse: { status: '400', description: 'Bad Request - validation failure', contentType: 'JSON', content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/eligibility/future' } }
      }
    ],
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' }
  },

  'payout-preview': {
    title: 'Get Batch Payout Preview',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/preview',
        method: 'POST',
        tag: 'Payout',
        title: 'Get Batch Payout Preview',
        description: 'Returns the fee, VAT and net amount for each transfer without moving money. The fee comes from the product slab, VAT is 5% of the fee for AED, and net amount is transaction amount plus fee plus VAT. Monthly and min/max limits are validated as part of the preview.',
        operationId: 'payoutPreview',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          {
            name: 'transfers',
            required: true,
            description: 'One entry per beneficiary to preview. At least one is required.',
            schema: {
              type: 'array of object',
              fields: [
                { name: 'beneficiaryId', required: true, description: 'ID of an active beneficiary owned by the caller.', schema: { type: 'integer', example: 101 } },
                { name: 'amount', required: true, description: 'Transfer amount. Must sit within minTransactionAmount and maxTransactionAmount.', schema: { type: 'number', example: 250.5 } },
                { name: 'remarks', required: false, description: 'Free-text remarks for this transfer.', schema: { type: 'string', example: 'Vendor payment' } }
              ],
              example: [{beneficiaryId: 1362, amount: 250.5}]
            }
          }
        ],
        responses: [
          {
            status: '200',
            description: 'Success',
            content: {
              response: {
                data: {
                  previews: [
                    { beneficiaryId: 101, beneficiaryName: 'Ahmed Al Mansouri', accountNumberMasked: '****3456', bankName: 'Abu Dhabi Commercial Bank', transactionAmount: 250.5, feeAmount: 5.0, vatAmount: 0.25, netAmount: 255.75, remarks: 'Vendor payment', currency: 'AED' }
                  ],
                  summary: { totalTransactions: 1, totalTransactionAmount: 250.5, totalFeeAmount: 5.0, totalVatAmount: 0.25, totalNetAmount: 255.75, currency: 'AED', minTransactionAmount: 10.0, maxTransactionAmount: 50000.0, minTotalAmount: 10.0, maxTotalAmount: 100000.0 }
                },
                status: 200,
                httpStatus: 'OK',
                message: 'Payout preview generated successfully'
              }
            },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'previews', type: 'object', description: 'One preview per requested transfer' },
              { name: 'previews[].feeAmount', type: 'number', description: 'Service charge resolved from the product slab' },
              { name: 'previews[].vatAmount', type: 'number', description: 'VAT on the fee - 5% for AED' },
              { name: 'previews[].netAmount', type: 'number', description: 'transactionAmount + feeAmount + vatAmount' },
              { name: 'summary.totalTransactions', type: 'integer', description: 'Number of transfers in the batch' },
              { name: 'summary.totalNetAmount', type: 'number', description: 'Total debited from the wallet if the batch is executed' },
              { name: 'summary.currency', type: 'string', description: 'Settlement currency' },
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'response.message', type: 'string', description: 'Human-readable result message' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request - validation failure',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/preview' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized - missing or expired access token',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/preview' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '403',
            description: 'Forbidden - no encryption key mapping for this user in the selected environment',
            content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Payout preview generated successfully',
          contentType: 'JSON',
          content: {
            response: {
              data: {
                previews: [
                  { beneficiaryId: 101, beneficiaryName: 'Ahmed Al Mansouri', accountNumberMasked: '****3456', bankName: 'Abu Dhabi Commercial Bank', transactionAmount: 250.5, feeAmount: 5.0, vatAmount: 0.25, netAmount: 255.75, remarks: 'Vendor payment', currency: 'AED' }
                ],
                summary: { totalTransactions: 1, totalTransactionAmount: 250.5, totalFeeAmount: 5.0, totalVatAmount: 0.25, totalNetAmount: 255.75, currency: 'AED', minTransactionAmount: 10.0, maxTransactionAmount: 50000.0, minTotalAmount: 10.0, maxTotalAmount: 100000.0 }
              },
              status: 200,
              httpStatus: 'OK',
              message: 'Payout preview generated successfully'
            }
          }
        },
        failureResponse: { status: '400', description: 'Bad Request - validation failure', contentType: 'JSON', content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/preview' } }
      }
    ],
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' }
  },

  'payout-transfer': {
    title: 'Initiate Payout Transfer',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/transfer',
        method: 'POST',
        tag: 'Payout',
        title: 'Initiate Payout Transfer',
        description: 'Executes one or more payouts immediately. Validates the daily count and monthly value limits, then per transfer the amount limits and beneficiary ownership. The request is encrypted with the caller’s static key and forwarded to the Transaction service /ext/transact endpoint with initiatedFrom MERCHANT_PORTAL and sourceOfPayment WEB. Requires x-latitude and x-longitude headers.',
        operationId: 'payoutTransfer',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'x-latitude', in: 'header', required: true, description: 'Latitude of the device initiating the payout.', schema: { type: 'string', example: '25.2048' } },
          { name: 'x-longitude', in: 'header', required: true, description: 'Longitude of the device initiating the payout.', schema: { type: 'string', example: '55.2708' } }
        ],
        requestBody: null,
        requestBodyFields: [
          {
            name: 'transfers',
            required: true,
            description: 'One entry per beneficiary to pay. At least one is required.',
            schema: {
              type: 'array of object',
              fields: [
                { name: 'beneficiaryId', required: true, description: 'ID of an active beneficiary owned by the caller.', schema: { type: 'integer', example: 101 } },
                { name: 'amount', required: true, description: 'Transfer amount. Must sit within minTransactionAmount and maxTransactionAmount.', schema: { type: 'number', example: 250.5 } },
                { name: 'remarks', required: false, description: 'Free-text remarks for this transfer.', schema: { type: 'string', example: 'Vendor payment' } }
              ],
              example: [{beneficiaryId: 1362, amount: 250.5}]
            }
          },
          { name: 'idempotencyKey', required: false, description: 'Replay guard. Re-sending the same key returns the original result with duplicateRequest set to true.', schema: { type: 'string', example: 'a3f1c9e2-4b7d-4f8a-9c11-2d5e6f7a8b90' } },
          { name: 'isScheduledPayout', required: false, description: 'Marks the batch as originating from a schedule. Defaults to false.', schema: { type: 'boolean', example: false } },
          { name: 'sourceOfPayment', required: false, description: 'Payment source. Defaults to WEB.', schema: { type: 'string', example: 'WEB', enum: ['WEB', 'APP'] } }
        ],
        responses: [
          {
            status: '200',
            description: 'Success',
            content: {
              response: {
                data: {
                  results: [
                    { senderName: 'Pay10 Merchant FZE', beneficiaryId: 101, beneficiaryName: 'Ahmed Al Mansouri', accountNumberMasked: '****3456', bankName: 'Abu Dhabi Commercial Bank', amount: 250.5, transactionId: 'TXN2608241141020001', payoutTransactionId: 'PO2608241141020001', referenceId: 'REF-8f21c4', status: 'SUCCESS', payoutStatus: 'INITIATED', message: 'Payout initiated successfully', fee: 5.0, vat: 0.25, netAmount: 255.75, totalAmount: 255.75, currency: 'AED', walletBalanceBefore: 10000.0, walletBalanceAfter: 9744.25, createDate: '2026-08-24T11:41:02Z' }
                  ],
                  summary: { totalTransactions: 1, successfulTransactions: 1, failedTransactions: 0, pendingTransactions: 0, totalAmount: 255.75, currency: 'AED' },
                  idempotencyKey: 'a3f1c9e2-4b7d-4f8a-9c11-2d5e6f7a8b90',
                  duplicateRequest: false,
                  message: 'Batch payout processed'
                },
                status: 200,
                httpStatus: 'OK',
                message: 'Payout transfer processed'
              }
            },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'results', type: 'object', description: 'One result per transfer in the batch' },
              { name: 'results[].transactionId', type: 'string', description: 'Wallet transaction ID' },
              { name: 'results[].payoutTransactionId', type: 'string', description: 'Payout transaction ID' },
              { name: 'results[].status', type: 'string', description: 'SUCCESS, FAILED or PENDING' },
              { name: 'results[].payoutStatus', type: 'string', description: 'INITIATED, PROCESSING, COMPLETED or FAILED' },
              { name: 'results[].fee', type: 'number', description: 'Service charge applied' },
              { name: 'results[].vat', type: 'number', description: 'VAT on the fee' },
              { name: 'results[].walletBalanceAfter', type: 'number', description: 'Wallet balance after this transfer' },
              { name: 'summary.successfulTransactions', type: 'integer', description: 'Count of transfers that succeeded' },
              { name: 'summary.failedTransactions', type: 'integer', description: 'Count of transfers that failed' },
              { name: 'idempotencyKey', type: 'string', description: 'Echo of the supplied idempotency key' },
              { name: 'duplicateRequest', type: 'boolean', description: 'True when this key was already processed - results are the original ones' },
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'response.message', type: 'string', description: 'Human-readable result message' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request - validation failure',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/transfer' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized - missing or expired access token',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/transfer' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '403',
            description: 'Forbidden - no encryption key mapping for this user in the selected environment',
            content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: {
          status: '200',
          description: 'Payout transfer processed',
          contentType: 'JSON',
          content: {
            response: {
              data: {
                results: [
                  { senderName: 'Pay10 Merchant FZE', beneficiaryId: 101, beneficiaryName: 'Ahmed Al Mansouri', accountNumberMasked: '****3456', bankName: 'Abu Dhabi Commercial Bank', amount: 250.5, transactionId: 'TXN2608241141020001', payoutTransactionId: 'PO2608241141020001', referenceId: 'REF-8f21c4', status: 'SUCCESS', payoutStatus: 'INITIATED', message: 'Payout initiated successfully', fee: 5.0, vat: 0.25, netAmount: 255.75, totalAmount: 255.75, currency: 'AED', walletBalanceBefore: 10000.0, walletBalanceAfter: 9744.25, createDate: '2026-08-24T11:41:02Z' }
                ],
                summary: { totalTransactions: 1, successfulTransactions: 1, failedTransactions: 0, pendingTransactions: 0, totalAmount: 255.75, currency: 'AED' },
                idempotencyKey: 'a3f1c9e2-4b7d-4f8a-9c11-2d5e6f7a8b90',
                duplicateRequest: false,
                message: 'Batch payout processed'
              },
              status: 200,
              httpStatus: 'OK',
              message: 'Payout transfer processed'
            }
          }
        },
        failureResponse: { status: '400', description: 'Bad Request - validation failure', contentType: 'JSON', content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 400, error: 'Bad Request', message: 'Validation failed', path: '/merchant-portal/payout/dp/transfer' } }
      }
    ],
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' }
  },

  'payout-webhook': {
    title: 'Webhook Payout Transaction Status',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    note: { content: 'Note: this page documents the callback Pay10 sends to you - there is no endpoint here to call. Configure your payout callback URL in webhook settings, then use Transfer Now to generate a real notification.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' },
    endpoints: [
      {
        path: '',
        method: '',
        tag: 'Payout',
        title: 'Webhook Payout Transaction Status',
        description: 'Pay10 POSTs a notification to your configured callback URL each time a payout changes state - when it settles, and when it fails. Payouts are asynchronous: Transfer Now returns INITIATED, and the final outcome arrives here. Treat the webhook as the source of truth for settlement, and reconcile with Status Enquiry if a notification is missed.',
        operationId: 'payoutWebhook',
        parameters: [],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Payload Pay10 sends to your callback URL',
            content: { transactionId: 'TXN2608241141020001', payoutTransactionId: 'PO2608241141020001', referenceId: 'REF-8f21c4', status: 'SUCCESS', payoutStatus: 'COMPLETED', beneficiaryName: 'Ahmed Al Mansouri', accountNumberMasked: '****3456', bankName: 'Abu Dhabi Commercial Bank', amount: 250.5, currency: 'AED', fee: 5.0, vat: 0.25, netAmount: 255.75, remarks: 'Vendor payment', eventType: 'PAYOUT_STATUS', createDate: '2026-08-24T11:41:02Z' },
            responseFields: [
              { name: 'transactionId', type: 'string', description: 'Wallet transaction id - match this against the id Transfer Now returned' },
              { name: 'payoutTransactionId', type: 'string', description: 'Payout transaction id' },
              { name: 'referenceId', type: 'string', description: 'Reference supplied to the transaction service' },
              { name: 'status', type: 'string', description: 'SUCCESS or FAILED' },
              { name: 'payoutStatus', type: 'string', description: 'COMPLETED or FAILED - the settled outcome' },
              { name: 'beneficiaryName', type: 'string', description: 'Beneficiary paid' },
              { name: 'accountNumberMasked', type: 'string', description: 'Masked account number' },
              { name: 'amount', type: 'number', description: 'Transfer amount' },
              { name: 'fee', type: 'number', description: 'Service charge applied' },
              { name: 'vat', type: 'number', description: 'VAT on the fee' },
              { name: 'netAmount', type: 'number', description: 'Total debited from the wallet' },
              { name: 'eventType', type: 'string', description: 'PAYOUT_STATUS' },
              { name: 'createDate', type: 'string', description: 'Timestamp of the state change' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Successful payout notification', contentType: 'JSON', content: { transactionId: 'TXN2608241141020001', status: 'SUCCESS', payoutStatus: 'COMPLETED', amount: 250.5, currency: 'AED', eventType: 'PAYOUT_STATUS' } },
        failureResponse: { status: '200', description: 'Failed payout notification', contentType: 'JSON', content: { transactionId: 'TXN2608241141020002', status: 'FAILED', payoutStatus: 'FAILED', amount: 250.5, currency: 'AED', message: 'Beneficiary bank rejected the transfer', eventType: 'PAYOUT_STATUS' } }
      }
    ]
  },

  'payout-wallet-balance': {
    title: 'Wallet Balance Check',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' },
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/wallet-balance',
        method: 'GET',
        tag: 'Payout',
        title: 'Wallet Balance Check',
        description: 'Returns the balance of the Pay10 Biz Wallet that funds payouts. Read this before Payout Preview or Transfer Now: the transfer flow validates the wallet balance against the batch net amount, so a batch larger than the balance is rejected at execution.',
        operationId: 'payoutWalletBalance',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Wallet balance fetched successfully',
            content: { response: { data: { balance: 9744.25, currencyCode: 'AED', walletId: '****8931' }, status: 200, httpStatus: 'OK', message: 'Wallet balance fetched successfully' } },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'balance', type: 'number', description: 'Available balance that can fund payouts' },
              { name: 'currencyCode', type: 'string', description: 'Currency of the balance, e.g. AED' },
              { name: 'walletId', type: 'string', description: 'Wallet identifier, masked' },
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'response.message', type: 'string', description: 'Human-readable result message' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized \- missing or expired access token',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/wallet-balance' },
            responseFields: [
              { name: 'timestamp', type: 'string', description: 'Time of the error' },
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'error', type: 'string', description: 'Error type' },
              { name: 'message', type: 'string', description: 'Detailed error message' },
              { name: 'path', type: 'string', description: 'Request path that caused the error' }
            ]
          },
          {
            status: '403',
            description: 'Forbidden \- no encryption key mapping for this user in the selected environment',
            content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'httpStatus', type: 'string', description: 'HTTP status name' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Wallet balance fetched successfully', contentType: 'JSON', content: { response: { data: { balance: 9744.25, currencyCode: 'AED', walletId: '****8931' }, status: 200, httpStatus: 'OK', message: 'Wallet balance fetched successfully' } } },
        failureResponse: { status: '403', description: 'Forbidden \- no encryption key mapping for this user', contentType: 'JSON', content: { status: 403, httpStatus: 'FORBIDDEN', message: 'User is not found' } }
      }
    ]
  },

  'payout-status-enquiry': {
    title: 'Transaction Status Enquiry',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    note: { content: 'Transport: this endpoint is wrapped by the backend encryption advice. Your request body is sent as {"encData","encKey"} and the response comes back in the same envelope - the Developer Portal encrypts and decrypts it for you, so the fields documented below are the plaintext shapes. A raw cURL copied from this page must apply the same envelope itself.', 'background-color': '#EEF2FF', 'text-color': '#3730A3', 'border-color': '#6366F1' },
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/status-enquiry',
        method: 'GET',
        tag: 'Payout',
        title: 'Transaction Status Enquiry',
        description: 'Returns the current status of a single payout, looked up by the transactionId returned from Transfer Now. Scoped to your merchant - an id belonging to another merchant returns 404, the same as an unknown id.',
        operationId: 'payoutStatusEnquiry',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'transactionId', in: 'query', required: true, description: 'The transactionId returned by Transfer Now.', schema: { type: 'string', example: 'TXN2608241141020001' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Payout status fetched successfully',
            content: { response: { data: { transactionId: 'TXN2608241141020001', payoutTransactionId: 'PO2608241141020001', referenceId: 'REF-8f21c4', status: 'SUCCESS', payoutStatus: 'COMPLETED', beneficiaryName: 'Ahmed Al Mansouri', accountNumberMasked: '****3456', bankName: 'Abu Dhabi Commercial Bank', amount: 250.5, currency: 'AED', fee: 5.0, vat: 0.25, netAmount: 255.75, walletBalanceAfter: 9744.25, createDate: '2026-08-24T11:41:02Z' }, status: 200, httpStatus: 'OK', message: 'Payout status fetched successfully' } },
            responseFields: [
              { name: 'response.data', type: 'object', description: 'Payload described below' },
              { name: 'transactionId', type: 'string', description: 'Wallet transaction id' },
              { name: 'payoutTransactionId', type: 'string', description: 'Payout transaction id' },
              { name: 'referenceId', type: 'string', description: 'Reference supplied to the transaction service' },
              { name: 'status', type: 'string', description: 'SUCCESS, FAILED or PENDING' },
              { name: 'payoutStatus', type: 'string', description: 'INITIATED, PROCESSING, COMPLETED or FAILED' },
              { name: 'amount', type: 'number', description: 'Transfer amount' },
              { name: 'fee', type: 'number', description: 'Service charge applied' },
              { name: 'vat', type: 'number', description: 'VAT on the fee' },
              { name: 'walletBalanceAfter', type: 'number', description: 'Wallet balance after this transfer' },
              { name: 'createDate', type: 'string', description: 'Transaction timestamp' }
            ]
          },
          {
            status: '400',
            description: 'Bad Request - transactionId missing',
            content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'transactionId is required' } },
            responseFields: [
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.message', type: 'string', description: 'Detailed error message' }
            ]
          },
          {
            status: '404',
            description: 'No payout found for that transaction id',
            content: { response: { status: 404, httpStatus: 'NOT_FOUND', message: 'No payout found for that transaction id' } },
            responseFields: [
              { name: 'response.status', type: 'integer', description: 'HTTP status code' },
              { name: 'response.message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Payout status fetched successfully', contentType: 'JSON', content: { response: { data: { transactionId: 'TXN2608241141020001', status: 'SUCCESS', payoutStatus: 'COMPLETED', amount: 250.5, currency: 'AED' }, status: 200, httpStatus: 'OK', message: 'Payout status fetched successfully' } } },
        failureResponse: { status: '404', description: 'No payout found for that transaction id', contentType: 'JSON', content: { response: { status: 404, httpStatus: 'NOT_FOUND', message: 'No payout found for that transaction id' } } }
      }
    ]
  },

  'payout-batch-template': {
    title: 'Download Bulk Payout Template',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches/template',
        method: 'GET',
        tag: 'Payout Bulk',
        title: 'Download Bulk Payout Template',
        description: 'Downloads the XLSX template used for file-based bulk payout (Payout Plus). Fill beneficiary rows, remove sample rows, then upload via Upload Bulk Payout File.',
        operationId: 'payoutBatchTemplate',
        responseType: 'blob',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'XLSX template file',
            content: { fileName: 'bulk_payout_template.xlsx', contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
            responseFields: [
              { name: '(binary)', type: 'file', description: 'Excel template bytes' }
            ]
          },
          {
            status: '401',
            description: 'Unauthorized',
            content: { timestamp: '2026-08-24T11:41:02.991+00:00', status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource', path: '/merchant-portal/payout/dp/batches/template' },
            responseFields: [
              { name: 'status', type: 'integer', description: 'HTTP status code' },
              { name: 'message', type: 'string', description: 'Detailed error message' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Template downloaded', contentType: 'XLSX', content: { fileName: 'bulk_payout_template.xlsx' } },
        failureResponse: { status: '401', description: 'Unauthorized', contentType: 'JSON', content: { status: 401, error: 'Unauthorized', message: 'Full authentication is required to access this resource' } }
      }
    ]
  },

  'payout-batch-upload': {
    title: 'Upload Bulk Payout File',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches/upload',
        method: 'POST',
        tag: 'Payout Bulk',
        title: 'Upload Bulk Payout File',
        description: 'Uploads a CSV or XLSX payout file, validates each row, and stages a PENDING batch. Money does not move until Confirm Bulk Payout Batch. Duplicate file names for the same merchant are rejected after a prior submit.',
        operationId: 'payoutBatchUpload',
        requestContentType: 'multipart/form-data',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'file', required: true, description: 'CSV or XLSX file matching the bulk payout template.', schema: { type: 'file', format: 'binary', example: 'bulk_payout.xlsx' } }
        ],
        responses: [
          {
            status: '200',
            description: 'File processed and batch staged',
            content: { response: { data: { batchId: 501, fileName: 'bulk_payout.xlsx', validationMessage: 'Processed', pay10WalletCount: 2, otherBankCount: 8, totalErrors: 1, totalRecipients: 10, totalPayoutAmount: 12500.0, currency: 'AED', rows: [] }, status: 200, httpStatus: 'OK', message: 'File processed successfully' } },
            responseFields: [
              { name: 'batchId', type: 'integer', description: 'Staged batch id used for confirm, detail and row edits' },
              { name: 'fileName', type: 'string', description: 'Uploaded file name' },
              { name: 'totalRecipients', type: 'integer', description: 'Row count in the file' },
              { name: 'totalErrors', type: 'integer', description: 'Rows that failed validation' },
              { name: 'totalPayoutAmount', type: 'number', description: 'Sum of payout amounts across rows' },
              { name: 'pay10WalletCount', type: 'integer', description: 'Pay10 wallet recipients' },
              { name: 'otherBankCount', type: 'integer', description: 'External bank recipients' },
              { name: 'currency', type: 'string', description: 'Currency code' },
              { name: 'rows', type: 'array', description: 'Empty on upload; load rows via Get Bulk Payout Batch Detail' }
            ]
          },
          {
            status: '400',
            description: 'Invalid or rejected file',
            content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Please upload a valid CSV or XLSX file.' } },
            responseFields: [
              { name: 'response.message', type: 'string', description: 'Rejection reason' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'File processed successfully', contentType: 'JSON', content: { response: { data: { batchId: 501, fileName: 'bulk_payout.xlsx', totalRecipients: 10, totalErrors: 1, totalPayoutAmount: 12500.0, currency: 'AED' }, status: 200, httpStatus: 'OK', message: 'File processed successfully' } } },
        failureResponse: { status: '400', description: 'Invalid file', contentType: 'JSON', content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Please upload a valid CSV or XLSX file.' } } }
      }
    ]
  },

  'payout-batch-confirm': {
    title: 'Confirm Bulk Payout Batch',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches/{batchId}/confirm',
        method: 'POST',
        tag: 'Payout Bulk',
        title: 'Confirm Bulk Payout Batch',
        description: 'Claims a PENDING batch and starts asynchronous money movement for valid rows. Requires x-latitude and x-longitude. Poll Get Bulk Payout Batch Detail while status is IN_PROGRESS. Already-processed batches are rejected.',
        operationId: 'payoutBatchConfirm',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'batchId', in: 'path', required: true, description: 'Batch id from upload.', schema: { type: 'integer', example: 501 } },
          { name: 'x-latitude', in: 'header', required: true, description: 'Latitude of the device initiating the payout.', schema: { type: 'string', example: '25.2048' } },
          { name: 'x-longitude', in: 'header', required: true, description: 'Longitude of the device initiating the payout.', schema: { type: 'string', example: '55.2708' } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Batch accepted for processing',
            content: { response: { data: { batchId: 501, status: 'IN_PROGRESS', message: 'Processing', transferSummary: null, results: [] }, status: 200, httpStatus: 'OK', message: 'Batch processed' } },
            responseFields: [
              { name: 'batchId', type: 'integer', description: 'Confirmed batch id' },
              { name: 'status', type: 'string', description: 'IN_PROGRESS, SUCCESS, FAILED or PARTIALLY_FAILED' },
              { name: 'message', type: 'string', description: 'Human-readable status note' },
              { name: 'transferSummary', type: 'object', description: 'Aggregated transfer counts when available' },
              { name: 'results', type: 'array', description: 'Per-row transfer results when returned synchronously' }
            ]
          },
          {
            status: '400',
            description: 'Confirm rejected',
            content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Location headers (x-latitude/x-longitude) are required' } },
            responseFields: [
              { name: 'response.message', type: 'string', description: 'Rejection reason' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Batch processed', contentType: 'JSON', content: { response: { data: { batchId: 501, status: 'IN_PROGRESS', message: 'Processing' }, status: 200, httpStatus: 'OK', message: 'Batch processed' } } },
        failureResponse: { status: '400', description: 'Confirm rejected', contentType: 'JSON', content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Location headers (x-latitude/x-longitude) are required' } } }
      }
    ]
  },

  'payout-batch-list': {
    title: 'List Bulk Payout Batches',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches',
        method: 'GET',
        tag: 'Payout Bulk',
        title: 'List Bulk Payout Batches',
        description: 'Lists submitted payout batches for the merchant, with optional date and status filters.',
        operationId: 'payoutBatchList',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'startDate', in: 'query', required: false, description: 'Inclusive start date (ISO).', schema: { type: 'string', format: 'date', example: '2026-08-01' } },
          { name: 'endDate', in: 'query', required: false, description: 'Inclusive end date (ISO).', schema: { type: 'string', format: 'date', example: '2026-08-31' } },
          { name: 'status', in: 'query', required: false, description: 'Filter by batch status.', schema: { type: 'string', example: 'SUCCESS', enum: ['PENDING', 'IN_PROGRESS', 'SUCCESS', 'FAILED', 'PARTIALLY_FAILED'] } },
          { name: 'page', in: 'query', required: false, description: '1-based page.', schema: { type: 'integer', example: 1 } },
          { name: 'size', in: 'query', required: false, description: 'Page size.', schema: { type: 'integer', example: 12 } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Batch list',
            content: { response: { data: { batches: [{ batchId: 501, fileName: 'bulk_payout.xlsx', status: 'SUCCESS', totalAmount: 12500.0, currency: 'AED', totalRecipients: 10, totalErrors: 0 }], totalCount: 1, page: 1, size: 12 }, status: 200, httpStatus: 'OK', message: 'Payout batches fetched successfully' } },
            responseFields: [
              { name: 'batches', type: 'array', description: 'Batch summaries' },
              { name: 'batches[].batchId', type: 'integer', description: 'Batch id' },
              { name: 'batches[].status', type: 'string', description: 'Batch status' },
              { name: 'totalCount', type: 'integer', description: 'Total matching batches' },
              { name: 'page', type: 'integer', description: 'Current page' },
              { name: 'size', type: 'integer', description: 'Page size' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Payout batches fetched successfully', contentType: 'JSON', content: { response: { data: { batches: [{ batchId: 501, fileName: 'bulk_payout.xlsx', status: 'SUCCESS' }], totalCount: 1, page: 1, size: 12 }, status: 200, httpStatus: 'OK', message: 'Payout batches fetched successfully' } } },
        failureResponse: { status: '400', description: 'Invalid filter', contentType: 'JSON', content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Invalid date range' } } }
      }
    ]
  },

  'payout-batch-detail': {
    title: 'Get Bulk Payout Batch Detail',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches/{batchId}',
        method: 'GET',
        tag: 'Payout Bulk',
        title: 'Get Bulk Payout Batch Detail',
        description: 'Returns batch summary and a page of rows. Use while a confirmed batch is IN_PROGRESS, and to inspect validation errors before confirm.',
        operationId: 'payoutBatchDetail',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'batchId', in: 'path', required: true, description: 'Batch id.', schema: { type: 'integer', example: 501 } },
          { name: 'page', in: 'query', required: false, description: '1-based row page.', schema: { type: 'integer', example: 1 } },
          { name: 'size', in: 'query', required: false, description: 'Rows per page.', schema: { type: 'integer', example: 7 } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Batch detail',
            content: { response: { data: { batch: { batchId: 501, fileName: 'bulk_payout.xlsx', status: 'PENDING', totalAmount: 12500.0, currency: 'AED', totalRecipients: 10, totalErrors: 1 }, rows: [{ id: 9001, rowNumber: 1, beneficiaryName: 'Ahmed Al Mansouri', hasError: false, basicAmount: 250.5, currency: 'AED' }], totalRows: 10, page: 1, size: 7 }, status: 200, httpStatus: 'OK', message: 'Batch detail fetched successfully' } },
            responseFields: [
              { name: 'batch', type: 'object', description: 'Batch summary' },
              { name: 'rows', type: 'array', description: 'Paginated rows' },
              { name: 'rows[].id', type: 'integer', description: 'Row id for patch/delete' },
              { name: 'rows[].hasError', type: 'boolean', description: 'Whether the row failed validation' },
              { name: 'rows[].errorMessage', type: 'string', description: 'Validation error when hasError is true' },
              { name: 'totalRows', type: 'integer', description: 'Total rows in the batch' },
              { name: 'page', type: 'integer', description: 'Current page' },
              { name: 'size', type: 'integer', description: 'Page size' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Batch detail fetched successfully', contentType: 'JSON', content: { response: { data: { batch: { batchId: 501, status: 'PENDING' }, rows: [], totalRows: 10, page: 1, size: 7 }, status: 200, httpStatus: 'OK', message: 'Batch detail fetched successfully' } } },
        failureResponse: { status: '400', description: 'Batch not found', contentType: 'JSON', content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Payout batch not found' } } }
      }
    ]
  },

  'payout-batch-row-update': {
    title: 'Update Bulk Payout Batch Row',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches/{batchId}/rows/{rowId}',
        method: 'PATCH',
        tag: 'Payout Bulk',
        title: 'Update Bulk Payout Batch Row',
        description: 'Edits and revalidates a single PENDING batch row (for example a bad IBAN) without re-uploading the file. Null/blank fields are left unchanged.',
        operationId: 'payoutBatchRowUpdate',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'batchId', in: 'path', required: true, description: 'Batch id.', schema: { type: 'integer', example: 501 } },
          { name: 'rowId', in: 'path', required: true, description: 'Row id from batch detail.', schema: { type: 'integer', example: 9001 } }
        ],
        requestBody: null,
        requestBodyFields: [
          { name: 'iban', required: false, description: 'Corrected IBAN.', schema: { type: 'string', example: 'AE070331234567890123456' } },
          { name: 'beneficiaryName', required: false, description: 'Corrected beneficiary name.', schema: { type: 'string', example: 'Ahmed Al Mansouri' } },
          { name: 'mobile', required: false, description: 'Corrected mobile for Pay10 wallet rows.', schema: { type: 'string', example: '971501234567' } },
          { name: 'basicAmount', required: false, description: 'Corrected basic amount.', schema: { type: 'number', example: 250.5 } },
          { name: 'purposeLabel', required: false, description: 'Purpose label from the template list.', schema: { type: 'string', example: 'Salary' } }
        ],
        responses: [
          {
            status: '200',
            description: 'Row revalidated',
            content: { response: { data: { row: { id: 9001, hasError: false, basicAmount: 250.5 }, batch: { batchId: 501, totalErrors: 0 } }, status: 200, httpStatus: 'OK', message: 'Row revalidated' } },
            responseFields: [
              { name: 'row', type: 'object', description: 'Updated row' },
              { name: 'batch', type: 'object', description: 'Recomputed batch aggregates' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Row revalidated', contentType: 'JSON', content: { response: { data: { row: { id: 9001, hasError: false }, batch: { batchId: 501, totalErrors: 0 } }, status: 200, httpStatus: 'OK', message: 'Row revalidated' } } },
        failureResponse: { status: '400', description: 'Row update failed', contentType: 'JSON', content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Batch is not PENDING' } } }
      }
    ]
  },

  'payout-batch-row-delete': {
    title: 'Delete Bulk Payout Batch Row',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches/{batchId}/rows/{rowId}',
        method: 'DELETE',
        tag: 'Payout Bulk',
        title: 'Delete Bulk Payout Batch Row',
        description: 'Removes a single row from a PENDING batch and recomputes batch aggregates.',
        operationId: 'payoutBatchRowDelete',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'batchId', in: 'path', required: true, description: 'Batch id.', schema: { type: 'integer', example: 501 } },
          { name: 'rowId', in: 'path', required: true, description: 'Row id from batch detail.', schema: { type: 'integer', example: 9001 } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Row deleted',
            content: { response: { data: { row: null, batch: { batchId: 501, totalRecipients: 9, totalErrors: 0 } }, status: 200, httpStatus: 'OK', message: 'Row deleted' } },
            responseFields: [
              { name: 'batch', type: 'object', description: 'Recomputed batch aggregates' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Row deleted', contentType: 'JSON', content: { response: { data: { batch: { batchId: 501, totalRecipients: 9 } }, status: 200, httpStatus: 'OK', message: 'Row deleted' } } },
        failureResponse: { status: '400', description: 'Row delete failed', contentType: 'JSON', content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Batch is not PENDING' } } }
      }
    ]
  },

  'payout-batch-delete': {
    title: 'Delete Bulk Payout Batch',
    version: '1.0',
    serverUrl: '',
    schemas: {},
    endpoints: [
      {
        isFromPG: false,
        path: '/merchant-portal/payout/dp/batches/{batchId}',
        method: 'DELETE',
        tag: 'Payout Bulk',
        title: 'Delete Bulk Payout Batch',
        description: 'Deletes a PENDING batch that has not been confirmed.',
        operationId: 'payoutBatchDelete',
        parameters: [
          { name: 'Authorization', in: 'header', required: true, description: 'Bearer access token', schema: { type: 'string', example: 'Bearer eyJhbGciOiJIUzI1NiJ9' } },
          { name: 'batchId', in: 'path', required: true, description: 'Batch id.', schema: { type: 'integer', example: 501 } }
        ],
        requestBody: null,
        requestBodyFields: [],
        responses: [
          {
            status: '200',
            description: 'Batch deleted',
            content: { response: { data: null, status: 200, httpStatus: 'OK', message: 'Batch deleted successfully' } },
            responseFields: [
              { name: 'response.message', type: 'string', description: 'Result message' }
            ]
          }
        ],
        successResponse: { status: '200', description: 'Batch deleted successfully', contentType: 'JSON', content: { response: { status: 200, httpStatus: 'OK', message: 'Batch deleted successfully' } } },
        failureResponse: { status: '400', description: 'Batch delete failed', contentType: 'JSON', content: { response: { status: 400, httpStatus: 'BAD_REQUEST', message: 'Only PENDING batches can be deleted' } } }
      }
    ]
  }
};
